// @ts-nocheck
import { useState } from 'react';
// eslint-disable-next-line ferp-subgraph-ui/no-direct-mantine-import
import {
  Stack,
  Paper,
  Group,
  Text,
  Button,
  SimpleGrid,
  Select,
  ThemeIcon,
  Badge,
  Loader,
  Center,
} from '@mantine/core';
import {
  IconReportAnalytics,
  IconCurrencyDollar,
  IconUsers,
  IconClock,
  IconCalendar,
  IconShieldCheck,
  IconPlayerPlay,
} from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { PageHeader } from '../../components/PageHeader';
import { EmptyState } from '../../components/EmptyState';
import { useReports, useGenerateReport } from '../../hooks/useReports';

interface SavedReport {
  id: string;
  name: string;
  reportType: string;
}

const REPORT_TYPE_OPTIONS = [
  { value: 'payroll_summary', label: 'Payroll Summary' },
  { value: 'payroll_detail', label: 'Payroll Detail' },
  { value: 'tax_summary', label: 'Tax Summary' },
  { value: 'employee_detail', label: 'Employee Detail' },
  { value: 'time_summary', label: 'Time Summary' },
  { value: 'exception_summary', label: 'Exception Summary' },
  { value: 'schedule_summary', label: 'Schedule Summary' },
  { value: 'pto_balance', label: 'PTO Balance' },
  { value: 'w2_preview', label: 'W-2 Preview' },
  { value: 'aca_compliance', label: 'ACA Compliance' },
];

const REPORT_TYPE_ICONS: Record<string, React.ReactNode> = {
  payroll_summary: <IconCurrencyDollar size={20} />,
  payroll_detail: <IconCurrencyDollar size={20} />,
  tax_summary: <IconCurrencyDollar size={20} />,
  employee_detail: <IconUsers size={20} />,
  time_summary: <IconClock size={20} />,
  exception_summary: <IconClock size={20} />,
  schedule_summary: <IconCalendar size={20} />,
  pto_balance: <IconCalendar size={20} />,
  w2_preview: <IconShieldCheck size={20} />,
  aca_compliance: <IconShieldCheck size={20} />,
};

const REPORT_TYPE_COLORS: Record<string, string> = {
  payroll_summary: 'green',
  payroll_detail: 'green',
  tax_summary: 'orange',
  employee_detail: 'blue',
  time_summary: 'teal',
  exception_summary: 'red',
  schedule_summary: 'indigo',
  pto_balance: 'cyan',
  w2_preview: 'yellow',
  aca_compliance: 'pink',
};

function getReportTypeLabel(type: string): string {
  const opt = REPORT_TYPE_OPTIONS.find((o) => o.value === type);
  return opt?.label || type.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

export default function ReportsPage() {
  const navigate = useNavigate();
  const { data, loading } = useReports();
  const generateMutation = useGenerateReport();
  const [selectedType, setSelectedType] = useState<string | null>(null);

  const savedReports = (data ?? []) as unknown as SavedReport[];

  const handleGenerate = async (reportType: string) => {
    try {
      const result = await generateMutation.mutateAsync({ reportType });
      const r = result as { generateReport?: { id?: string } };
      if (r.generateReport?.id) {
        navigate(`/reports/${r.generateReport.id}`);
      }
    } catch {
      // error handled by mutation
    }
  };

  return (
    <Stack gap="md">
      <PageHeader
        title="Reports"
        subtitle="Generate and view workforce reports"
        breadcrumbs={[{ label: 'Reports' }]}
      />

      <Paper withBorder p="lg" radius="md">
        <Text fw={600} size="lg" mb="md">
          Generate Report
        </Text>

        <Group gap="sm" mb="md">
          <Select
            placeholder="Select report type"
            data={REPORT_TYPE_OPTIONS}
            value={selectedType}
            onChange={setSelectedType}
            searchable
            style={{ flex: 1, maxWidth: 400 }}
          />
          <Button
            icon={<IconPlayerPlay size={16} />}
            disabled={!selectedType}
            loading={generateMutation.isPending}
            onClick={() => selectedType && handleGenerate(selectedType)}
          >
            Generate
          </Button>
        </Group>

        <SimpleGrid cols={{ base: 2, sm: 3, md: 5 }} spacing="sm">
          {REPORT_TYPE_OPTIONS.map((opt) => (
            <Paper
              key={opt.value}
              withBorder
              p="sm"
              radius="sm"
              style={{ cursor: 'pointer', textAlign: 'center' }}
              onClick={() => handleGenerate(opt.value)}
            >
              <ThemeIcon
                size="lg"
                variant="light"
                color={REPORT_TYPE_COLORS[opt.value] || 'gray'}
                mx="auto"
                mb="xs"
              >
                {REPORT_TYPE_ICONS[opt.value] || <IconReportAnalytics size={20} />}
              </ThemeIcon>
              <Text size="xs" fw={500}>
                {opt.label}
              </Text>
            </Paper>
          ))}
        </SimpleGrid>
      </Paper>

      <Paper withBorder p="lg" radius="md">
        <Text fw={600} size="lg" mb="md">
          Saved Reports
        </Text>

        {loading ? (
          <Center py="xl">
            <Loader size="lg" />
          </Center>
        ) : savedReports.length === 0 ? (
          <EmptyState
            icon={<IconReportAnalytics size={48} stroke={1.5} color="var(--mantine-color-dimmed)" />}
            title="No Saved Reports"
            message="Generate a report to see it here."
          />
        ) : (
          <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }}>
            {savedReports.map((report) => (
              <Paper
                key={report.id}
                withBorder
                p="md"
                radius="sm"
                style={{ cursor: 'pointer' }}
                onClick={() => navigate(`/reports/${report.id}`)}
              >
                <Group justify="space-between">
                  <div>
                    <Text size="sm" fw={500}>
                      {report.name}
                    </Text>
                    <Badge
                      size="xs"
                      variant="light"
                      color={REPORT_TYPE_COLORS[report.reportType] || 'gray'}
                      mt={4}
                    >
                      {getReportTypeLabel(report.reportType)}
                    </Badge>
                  </div>
                  <ThemeIcon
                    size="sm"
                    variant="light"
                    color={REPORT_TYPE_COLORS[report.reportType] || 'gray'}
                  >
                    {REPORT_TYPE_ICONS[report.reportType] || <IconReportAnalytics size={14} />}
                  </ThemeIcon>
                </Group>
              </Paper>
            ))}
          </SimpleGrid>
        )}
      </Paper>
    </Stack>
  );
}
